import { useState, useEffect, useCallback, Fragment } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { prefetchMap } from './routes'
import {
  AppBar,
  Drawer as MuiDrawer,
  FormControl,
  IconButton,
  Select,
  Box,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  Avatar,
  Typography,
  Popover,
  ListItem,
  ListItemIcon
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { changeLanguagePref, getLanguageString } from './utilities/language'
import { getUserManagedCustomers } from './utilities/common'
import { useCustomer, useToken, useSnackbar } from './utilities/hooks'
import { userLogout } from './actions/userActions'

import MenuIcon from '@mui/icons-material/Menu'
import AccountIcon from '@mui/icons-material/AccountCircle'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ErrorBoundary from './components/ErrorBoundary'
import DynamicIcon from './components/DynamicIcon'
import UniChatBox from './components/UniChatBox'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft'
import SettingsIcon from '@mui/icons-material/Settings'
import InfoIcon from '@mui/icons-material/Info'
import LogoutIcon from '@mui/icons-material/Logout'

const appbarHeight = 64
const drawerWidth = 260
const drawerWidthClosed = 64

const Container = styled('div')(() => ({
  display: 'flex',
  height: '100vh',
  overflow: 'auto'
}))

const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `${drawerWidthClosed}px`
})
const Appbar = styled(AppBar)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: '#273340',
  zIndex: theme.zIndex.drawer + 1
}))
const Main = styled('main', { shouldForwardProp: prop => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    overflow: 'auto',
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: 0,
    width: '100%',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
          }),
          marginLeft: 0,
          width: `calc(100% - ${open ? drawerWidth : drawerWidthClosed}px)`
        }
      }
    ]
  })
)

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})(({ theme, open }) => ({
  // width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open
    ? {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme)
      }
    : {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme)
      })
}))

const Frame = props => {
  const { customerData, updateCustomerData } = useCustomer()
  const [customerList, setCustomerList] = useState([])
  const [sideBarHover, setSideBarHover] = useState(false)
  const [buttonHover, setButtonHover] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const [open, setOpen] = useState(true)
  const [expand, setExpand] = useState('')
  const { i18n } = useTranslation('common')
  const { authorizedMenuItems, username, email } = useToken()
  const location = useLocation()
  const history = useHistory()
  const { showAlertSnackbar } = useSnackbar()
  const userPopoverOpen = Boolean(anchor)
  const userPopoverId = userPopoverOpen ? 'user-popover' : undefined

  const setDefaultCustomer = useCallback(
    data => {
      updateCustomerData(data)
      window.localStorage.setItem('customer_data', JSON.stringify(data))
    },
    [updateCustomerData]
  )

  const handleClickUser = event => {
    setAnchor(event.currentTarget)
  }

  const changeLanguage = () => {
    if (i18n.language === 'en') {
      changeLanguagePref(i18n, 'cn')
    } else {
      changeLanguagePref(i18n, 'en')
    }
  }

  const handleLogout = async () => {
    try {
      await userLogout()
    } catch (error) {
      showAlertSnackbar({
        type: 'error',
        message: error?.message ?? 'Logout failed, please try again later.'
      })
    }
  }

  useEffect(() => {
    const customerList = getUserManagedCustomers()
    if (customerList.length) {
      setCustomerList(customerList)
      if (!customerData.id) {
        setDefaultCustomer(customerList[0])
      }
    }
  }, [customerData.id, setDefaultCustomer])

  const initExpandMenu = () => {
    const activeMenu = authorizedMenuItems.find(item => {
      const pathname = '/' + item.id
      if (item.subitems) {
        return item.subitems.some(subitem => {
          const subitemPathname = '/' + subitem.id
          return (
            location.pathname.startsWith(subitemPathname) ||
            (subitem.subitems &&
              subitem.subitems.some(i => location.pathname === `/${i.id}`))
          )
        })
      }
      return false
    })
    if (activeMenu) {
      setExpand(activeMenu.id)
    }
  }

  useEffect(() => {
    initExpandMenu()
  }, [])

  return (
    <Container>
      <Box
        component='img'
        src='./images/main_bg.png'
        alt='Background'
        sx={{
          display: { xs: 'none', sm: 'none', md: 'block' },
          height: '100vh',
          objectFit: 'cover',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: -1
        }}
      />
      <Drawer
        open={open}
        onMouseEnter={() => setSideBarHover(true)}
        onMouseLeave={() => setSideBarHover(false)}
        sx={{
          // width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            // width: drawerWidth + 'px',
            height: `100vh`,
            boxSizing: 'border-box',
            overflow: 'visible'
          },
          position: 'relative'
        }}
        variant='permanent'
        anchor='left'>
        {open ? (
          <div
            style={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column'
            }}>
            <Box
              component='img'
              src={'./images/icons/logo.png'}
              alt='Icon'
              sx={{ width: '210px', margin: '24px auto' }}
            />
            <Box
              sx={{
                display: 'flex',
                width: '38px',
                height: '19px',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '16px',
                border: '1.5px solid #000',
                position: 'absolute',
                bottom: '0px',
                right: '24px'
              }}>
              <Typography
                sx={{
                  color: '#000',
                  textAlign: 'center',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '100%'
                }}>
                {import.meta.env.VITE_APP_COUNTRY === 'US' ? 'US' : 'CA'}
              </Typography>
            </Box>
          </div>
        ) : (
          <Collapse in={!open} timeout={300} sx={{ textAlign: 'center' }}>
            <Box
              component='img'
              src={'./images/icons/logo_outlined.png'}
              alt='Icon'
              sx={{ width: '32px', margin: '24px auto' }}
            />
          </Collapse>
        )}
        <Collapse in={sideBarHover} timeout={300}>
          <IconButton
            onClick={() => setOpen(!open)}
            size='large'
            sx={{
              backgroundColor: buttonHover ? '#3BC9A0' : '#4FE1B7',
              color: '#fff',
              height: '20px',
              width: '20px',
              position: 'absolute',
              top: '80px',
              left: 'calc(100% - 10px)',
              pointerEvents: 'none',
              transition: 'background-color 200ms'
            }}>
            {open ? <ArrowLeftIcon /> : <ArrowRightIcon />}
          </IconButton>{' '}
        </Collapse>
        {/* Transparent clickable overlay for the button */}
        <Box
          onClick={() => setOpen(!open)}
          onMouseEnter={() => setButtonHover(true)}
          onMouseLeave={() => setButtonHover(false)}
          sx={{
            position: 'absolute',
            top: '75px',
            left: 'calc(100% - 15px)',
            width: '35px',
            height: '35px',
            cursor: 'pointer',
            opacity: sideBarHover ? 1 : 0,
            pointerEvents: sideBarHover ? 'auto' : 'none',
            transition: 'opacity 300ms',
            zIndex: 1
          }}
        />

        <List
          sx={{
            padding: '50px 8px 50px 8px',
            maxHeight: 'calc(100vh - 160px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }
            }
          }}>
          {authorizedMenuItems.map(item => {
            const pathname = '/' + item.id
            const parentActive = location.pathname === pathname
            const parentHasChildrenActive = Boolean(
              item.subitems?.some(subitem => {
                const subitemPathname = '/' + subitem.id
                return (
                  location.pathname === subitemPathname ||
                  location.pathname.startsWith(subitemPathname + '/') ||
                  (subitem.subitems &&
                    subitem.subitems.some(
                      i => location.pathname === `/${i.id}`
                    ))
                )
              })
            )
            const expanded = expand === item.id
            return (
              <Fragment key={item.id}>
                <ListItemButton
                  onMouseEnter={() => {
                    const prefetch = prefetchMap[pathname]
                    if (prefetch)
                      prefetch().catch(err =>
                        console.warn('[prefetch]', pathname, err)
                      )
                  }}
                  onClick={() => {
                    setOpen(true)
                    item.subitems
                      ? setExpand(expanded ? '' : item.id)
                      : history.push(pathname)
                  }}
                  sx={{
                    justifyContent: open ? 'flex-start' : 'center',
                    color: parentActive ? 'secondary.main' : '#2a3848',
                    backgroundColor: parentActive
                      ? 'rgba(79, 225, 183, 0.08) !important'
                      : '#ffffff'
                  }}>
                  <DynamicIcon
                    iconName={item.iconName}
                    sx={{
                      height: 38,
                      width: 38,
                      p: open ? 1 : 0.5,
                      borderRadius: open ? 0 : 2,
                      color: parentActive
                        ? open
                          ? 'secondary.main'
                          : '#ffffff'
                        : parentHasChildrenActive
                          ? open
                            ? '#212121'
                            : '#ffffff'
                          : '#212121',
                      backgroundColor:
                        parentActive || parentHasChildrenActive
                          ? open
                            ? 'transparent'
                            : 'secondary.main'
                          : 'transparent'
                    }}
                  />

                  {
                    <Collapse in={open} timeout={300}>
                      <ListItemText
                        primaryTypographyProps={{
                          fontWeight: parentActive ? 600 : undefined
                        }}>
                        {item.title}
                      </ListItemText>
                    </Collapse>
                  }
                  {item.subitems && open ? (
                    expanded ? (
                      <ExpandLessIcon sx={{ marginLeft: 'auto' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ marginLeft: 'auto' }} />
                    )
                  ) : null}
                </ListItemButton>
                {item.subitems && open && (
                  <Collapse in={expanded}>
                    <List component='div' disablePadding>
                      {item.subitems.map(subitem => {
                        const subitemPathname = '/' + subitem.id
                        const subitemActive =
                          location.pathname === subitemPathname ||
                          (subitem.subitems &&
                            subitem.subitems.some(
                              i => location.pathname === `/${i.id}`
                            ))
                        return (
                          <ListItemButton
                            onMouseEnter={() => {
                              const prefetch = prefetchMap[subitemPathname]
                              if (prefetch)
                                prefetch().catch(err =>
                                  console.warn(
                                    '[prefetch]',
                                    subitemPathname,
                                    err
                                  )
                                )
                            }}
                            onClick={() => history.push(subitemPathname)}
                            key={subitem.id}
                            sx={{
                              ...(subitemActive
                                ? {
                                    color: 'secondary.deep',
                                    backgroundColor:
                                      'rgba(79, 225, 183, 0.08) !important'
                                  }
                                : {
                                    color: '#212121',
                                    backgroundColor: '#fff'
                                  })
                            }}>
                            <ListItemText
                              sx={{ paddingLeft: 4 }}
                              primaryTypographyProps={{
                                fontWeight: subitemActive ? 600 : undefined,
                                paddingLeft: 2,
                                fontSize: 'small'
                              }}>
                              {subitem.title}
                            </ListItemText>
                          </ListItemButton>
                        )
                      })}
                    </List>
                  </Collapse>
                )}
              </Fragment>
            )
          })}
        </List>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '68px',
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              height: '68px',
              alignItems: 'center',
              padding: open ? '0 20px' : '0 16px',
              cursor: 'pointer'
            }}
            onClick={handleClickUser}>
            <Avatar
              sx={{ height: '32px', width: '32px', marginRight: '16px' }}
              src='./images/icons/avatar.png'
            />
            {
              <Collapse in={open} timeout={50}>
                <Typography
                  variant='body'
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                  {username}
                </Typography>
              </Collapse>
            }
          </Box>
          <Popover
            id={userPopoverId}
            open={userPopoverOpen}
            anchorEl={anchor}
            onClose={() => setAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            sx={{ mt: 1 }}>
            <Box sx={{ width: 200 }}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: '16px' }}>{username}</Typography>
                <Typography variant='body2' color='text.secondary'>
                  {email}
                </Typography>
              </Box>
              <Divider />

              <List disablePadding>
                {/* <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                      <SettingsIcon color='secondary' />
                    </ListItemIcon>
                    <ListItemText primary='Settings' />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                      <InfoIcon color='secondary' />
                    </ListItemIcon>
                    <ListItemText primary='Help Center' />
                  </ListItemButton>
                </ListItem>

                <Divider component='li' /> */}

                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                      <LogoutIcon color='secondary' />
                    </ListItemIcon>
                    <ListItemText primary='Log Out' />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Popover>
        </Box>
      </Drawer>
      <Main style={{ padding: '16px 36px 16px 36px' }} open={open}>
        <ErrorBoundary key={location.pathname}>{props.children}</ErrorBoundary>
      </Main>
      <UniChatBox />
    </Container>
  )
}

export default Frame
